import { MarkNotificationReadQueueElement } from 'src/core/queue/domain/elements/mark-notification-read-queue-element';
import { NotificationRepository } from 'src/core/notifications/domain/notification-repository';
import { Injectable, Injector } from '@angular/core';
import { Queue } from './queue';
import { StorableService } from 'src/core/storage/domain/storable-service';
import { Initializable } from 'src/core/common/domain/initializer/initializable';
import { BehaviorSubject, Observable } from 'rxjs';
import { Storage } from 'src/core/storage/domain/storage';
import { QueueElementFactory } from './queue-element-factory';
import { QueueElement } from './queue-element';
import { EventsService } from 'src/core/events/events.service';
import { NumPendingElementsChangedEvent } from 'src/core/events/num-pending-elements-changed.event';
import { IsSynchronizingEvent } from 'src/core/events/is-synchronizing.event';
import { CloseTaskQueueElement } from './elements/close-task-queue-element';
import { CloseTask } from 'src/core/tasks/application/close/close-task';
import { TaskQueueElement } from './elements/task-queue-element';
import { CloseTaskRequest } from 'src/core/tasks/application/close/close-task-request';
import { StartTaskQueueElement } from './elements/start-task-queue-element';
import { StartTask } from 'src/core/tasks/application/start/start-task';
import { StartTaskRequest } from 'src/core/tasks/application/start/start-task-request';
import { RejectTaskQueueElement } from './elements/reject-task-queue-element';
import { StopTaskQueueElement } from './elements/stop-task-queue-element';
import { StopTask } from 'src/core/tasks/application/stop/stop-task';
import { StopTaskRequest } from 'src/core/tasks/application/stop/stop-task-request';
import { RejectTask } from 'src/core/tasks/application/reject/reject-task';
import { RejectTaskRequest } from 'src/core/tasks/application/reject/reject-task-request';
import { CreateTaskQueueElement } from './elements/create-task-queue-element';
import { CreateTask } from 'src/core/tasks/application/create/create-task';
import { CreateTaskRequest } from 'src/core/tasks/application/create/create-task-request';
import { Nullable } from 'src/core/common/domain/types/types';
import { TaskAlreadyClosedException } from 'src/core/common/domain/exceptions/task-already-closed-exception';
import { ConnectionException } from 'src/core/common/domain/exceptions/connection-exception';
import { ConfirmRejectTaskQueueElement } from './elements/confirm-reject-task-queue-element';
import { ConfirmRejectTask } from 'src/core/tasks/application/confirm-reject/confirm-reject-task';
import { ConfirmRejectTaskRequest } from 'src/core/tasks/application/confirm-reject/confirm-reject-task-request';
import { ConfirmCloseTaskQueueElement } from './elements/confirm-close-task-queue-element';
import { ConfirmCloseTask } from 'src/core/tasks/application/confirm-close/confirm-close-task';
import { ConfirmCloseTaskRequest } from 'src/core/tasks/application/confirm-close/confirm-close-task-request';

@Injectable({
  providedIn: 'root',
})
export class QueueService implements StorableService, Initializable {
  taskIds: string[] = [];
  isProcessing = false;
  private currentPass: Nullable<Promise<void>> = null;
  private lastExclusiveOperation: Promise<unknown> = Promise.resolve();
  private readonly heldElementIds = new Set<string>();
  private readonly elementIdsSkippedWhileHeld = new Set<string>();
  private readySubject = new BehaviorSubject<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  ready$: Observable<boolean> = this.readySubject.asObservable();
  private queue: Nullable<Queue> = null;
  private readonly KEY_QUEUE = 'queue';

  constructor(private storage: Storage, private eventsService: EventsService, private injector: Injector) {}

  async clear() {
    this.queue = null;
    await this.storage.remove(this.KEY_QUEUE);
    this.eventsService.publishNumPendingElementsChanged(new NumPendingElementsChangedEvent(0));
  }

  async init() {
    await this.initQueue();
    this.eventsService.publishNumPendingElementsChanged(new NumPendingElementsChangedEvent(this.getNumElements()));
    this.readySubject.next(true);
  }

  /**
   * A call made while a pass is running gets that same pass, so awaiting it always means the queue has
   * been processed.
   */
  processQueue(): Promise<void> {
    if (!this.currentPass) {
      this.currentPass = this.runExclusively(() => this.runPass()).finally(() => {
        this.currentPass = null;
      });
    }

    return this.currentPass;
  }

  hasQueueElementWithTaskId(taskId: string): boolean {
    return this.taskIds.includes(taskId);
  }

  getElements(): QueueElement[] {
    if (this.queue === null) {
      return [];
    }

    return this.queue.elements();
  }

  getNumElements(): number {
    if (this.queue === null) {
      return 0;
    }

    return this.getElements().filter((element) => element.isVisible()).length;
  }

  getPendingNotificationReadIds(): Array<string> {
    return this.getElements()
      .filter(
        (element): element is MarkNotificationReadQueueElement => element instanceof MarkNotificationReadQueueElement
      )
      .map((element) => element.notificationId);
  }

  async enqueue(element: QueueElement) {
    if (!this.elementCanBeEnqueued(element)) {
      return;
    }

    this.queue.enqueue(element);
    this.updateTaskIds();

    await this.saveQueue();
    this.eventsService.publishNumPendingElementsChanged(new NumPendingElementsChangedEvent(this.getNumElements()));
  }

  /**
   * Waits for a running pass to end, since the pass walks the queue by index.
   */
  removeElement(element: QueueElement): Promise<void> {
    return this.runExclusively(() => this.removeElementNow(element));
  }

  getElementsOfTask(taskId: string): TaskQueueElement[] {
    return this.taskElements().filter((element) => element.task.id === taskId);
  }

  /**
   * Waits for a running pass to end, like removeElement.
   */
  removeElementsOfTask(taskId: string): Promise<void> {
    return this.runExclusively(() => this.removeElementsNow(this.getElementsOfTask(taskId)));
  }

  /**
   * Keeps passes away from an element while the user corrects or discards it, since that flow sends or
   * removes it itself. Waits for a running pass, which may be processing that very element. Later elements
   * of the same task stay blocked behind it.
   */
  holdElement(element: QueueElement): Promise<void> {
    return this.runExclusively(async () => {
      this.heldElementIds.add(element.id);
    });
  }

  /**
   * A pass that skipped the element while it was held will not come back for it, so it is processed now,
   * unless the change already sent or removed it.
   */
  async releaseElement(element: QueueElement): Promise<void> {
    this.heldElementIds.delete(element.id);
    const wasSkipped = this.elementIdsSkippedWhileHeld.delete(element.id);
    const isStillQueued = this.getElements().some((queued) => queued.id === element.id);
    if (wasSkipped && isStillQueued) {
      await this.processQueue();
    }
  }

  async updateQueueElement(queueElement: QueueElement) {
    this.queue.updateElement(queueElement);
    await this.saveQueue();
  }

  async updateLocalTaskReferences(oldTaskId: string, newTaskId: string) {
    let hasChanged = false;
    for (const queueElement of this.queue.elements()) {
      if (queueElement instanceof TaskQueueElement) {
        if (queueElement.task.id === oldTaskId) {
          queueElement.task.changeId(newTaskId);
          queueElement.publicId = newTaskId;
          hasChanged = true;
        }
      }
    }
    if (hasChanged) {
      await this.saveQueue();
    }
  }

  isEmpty(): boolean {
    if (this.queue === null) {
      return true;
    }

    return this.queue.isEmpty();
  }

  private async processQueueElement(queueElement: QueueElement, index: number) {
    if (queueElement instanceof TaskQueueElement) {
      if (!this.taskQueueElementCanBeProcessed(queueElement, index)) {
        throw new Error('Element cannot be processed because other elements block this one');
      }
      await this.processTaskQueueElement(queueElement);
      return;
    }

    if (queueElement instanceof MarkNotificationReadQueueElement) {
      await this.injector.get(NotificationRepository).markRead(queueElement.notificationId);
      return;
    }

    // otherwise…
    throw new Error('Unsupported queue element');
  }

  private async runPass() {
    this.isProcessing = true;
    try {
      this.eventsService.publishIsSynchronizing(new IsSynchronizingEvent(true));

      let index = 0;
      // iterate all elements, one by one, in the retrieved order (from old to new)
      while (index < this.queueSize()) {
        const queueElement = this.queue.element(index);
        if (!queueElement) {
          break; // end of loop
        }
        if (this.heldElementIds.has(queueElement.id)) {
          this.elementIdsSkippedWhileHeld.add(queueElement.id);
          index++;
          continue;
        }

        try {
          await this.processQueueElement(queueElement, index);
          await this.removeProcessedElement(queueElement);
        } catch (exception) {
          if (exception instanceof ConnectionException) {
            // every remaining element would fail the same way, so the rest wait for the next pass
            await this.saveQueue();
            break;
          }
          if (exception instanceof TaskAlreadyClosedException) {
            // the server already has the task closed, so the element is discarded and the next one takes its index
            await this.removeProcessedElement(queueElement);
          } else {
            await this.saveQueue();
            index++;
          }
        }

        // add some delay because otherwise fake API server will fail
        // RS_SETTING comment when DIST (used in demos to see the synchronization queue)
        // await this.delay(1000);
        // RS_SETTING enable when DIST
        await this.delay(100);
      }
    } finally {
      this.isProcessing = false;
      this.eventsService.publishIsSynchronizing(new IsSynchronizingEvent(false));
    }
  }

  private runExclusively<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.lastExclusiveOperation.then(operation);
    this.lastExclusiveOperation = result.catch(() => undefined);

    return result;
  }

  private async removeElementNow(element: QueueElement) {
    await this.removeElementsNow([element]);
  }

  private async removeElementsNow(elements: QueueElement[]) {
    const idsToRemove = elements.map((element) => element.id);
    const remainingElements = this.getElements().filter((item) => !idsToRemove.includes(item.id));
    await this.storage.set(
      this.KEY_QUEUE,
      remainingElements.map((item) => item.toPrimitives())
    );
    for (const element of elements) {
      this.queue.removeElement(element);
    }
    this.updateTaskIds();
    this.eventsService.publishNumPendingElementsChanged(new NumPendingElementsChangedEvent(this.getNumElements()));
  }

  private taskElements(): TaskQueueElement[] {
    return this.getElements().filter((element): element is TaskQueueElement => element instanceof TaskQueueElement);
  }

  private async removeProcessedElement(queueElement: QueueElement) {
    this.queue.removeElement(queueElement);
    await this.saveQueue();
    this.updateTaskIds();
    this.eventsService.publishNumPendingElementsChanged(new NumPendingElementsChangedEvent(this.getNumElements()));
  }

  private delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, ms);
    });
  }

  private updateTaskIds() {
    const taskIds: string[] = [];
    for (const element of this.queue.elements()) {
      const isTaskQueueElement = element instanceof TaskQueueElement;
      if (isTaskQueueElement) {
        const taskId = element.task.id;
        if (!taskIds.includes(taskId)) {
          taskIds.push(taskId);
        }
      }
    }

    this.taskIds = taskIds;
  }

  private async initQueue() {
    const queue = await this.storage.get(this.KEY_QUEUE);
    if (queue === null) {
      // initialize an empty queue and save it into storage
      this.queue = new Queue();
      await this.saveQueue();
      return;
    }

    const initialElements = [];
    if (queue && Array.isArray(queue) && queue.length > 0) {
      for (const item of queue) {
        const element = QueueElementFactory.fromPayload(item);
        initialElements.push(element);
      }
    }
    this.queue = new Queue(initialElements);
    this.updateTaskIds();
  }

  private async saveQueue() {
    // TODO: Improve by not converting every time (or storing the stringified version of elements)
    const queue = this.queue._elements.map((q) => q.toPrimitives());
    await this.storage.set(this.KEY_QUEUE, queue);
  }

  private async processTaskQueueElement(element: TaskQueueElement) {
    if (element instanceof CloseTaskQueueElement) {
      await this.processCloseTaskQueueElement(element);
    } else if (element instanceof StartTaskQueueElement) {
      await this.processStartTaskQueueElement(element);
    } else if (element instanceof StopTaskQueueElement) {
      await this.processStopTaskQueueElement(element);
    } else if (element instanceof RejectTaskQueueElement) {
      await this.processRejectTaskQueueElement(element);
    } else if (element instanceof CreateTaskQueueElement) {
      await this.processCreateTaskQueueElement(element);
    } else if (element instanceof ConfirmCloseTaskQueueElement) {
      await this.processConfirmCloseTaskQueueElement(element);
    } else if (element instanceof ConfirmRejectTaskQueueElement) {
      await this.processConfirmRejectTaskQueueElement(element);
    } else {
      console.error('TaskQueueElement not implemented', element);
    }
  }

  private async processRejectTaskQueueElement(element: RejectTaskQueueElement) {
    const action = this.injector.get<RejectTask>(RejectTask);
    await action.execute(RejectTaskRequest.retry(element));
  }

  private async processConfirmRejectTaskQueueElement(element: ConfirmRejectTaskQueueElement) {
    const action = this.injector.get<ConfirmRejectTask>(ConfirmRejectTask);
    await action.execute(ConfirmRejectTaskRequest.retry(element));
  }

  private async processCreateTaskQueueElement(element: CreateTaskQueueElement) {
    const action = this.injector.get<CreateTask>(CreateTask);
    await action.execute(CreateTaskRequest.retry(element));
  }

  private async processStopTaskQueueElement(element: StopTaskQueueElement) {
    const action = this.injector.get<StopTask>(StopTask);
    await action.execute(StopTaskRequest.retry(element));
  }

  private async processStartTaskQueueElement(element: StartTaskQueueElement) {
    const action = this.injector.get<StartTask>(StartTask);
    await action.execute(StartTaskRequest.retry(element));
  }

  private async processCloseTaskQueueElement(element: CloseTaskQueueElement) {
    const action = this.injector.get<CloseTask>(CloseTask);
    await action.execute(CloseTaskRequest.retry(element));
  }

  private async processConfirmCloseTaskQueueElement(element: ConfirmCloseTaskQueueElement) {
    const action = this.injector.get<ConfirmCloseTask>(ConfirmCloseTask);
    await action.execute(ConfirmCloseTaskRequest.retry(element));
  }

  private elementCanBeEnqueued(element: QueueElement): boolean {
    if (element instanceof MarkNotificationReadQueueElement) {
      return !this.getPendingNotificationReadIds().includes(element.notificationId);
    }

    if (element instanceof CloseTaskQueueElement) {
      const taskId = element.task.id;
      // if there is not any other CloseTaskQueueElement with same taskId, element can be enqueued
      return (
        this.queue
          .elements()
          .filter((qe) => qe instanceof CloseTaskQueueElement)
          .filter((qe: CloseTaskQueueElement) => qe.task.id === taskId).length === 0
      );
    } else if (element instanceof RejectTaskQueueElement) {
      const taskId = element.task.id;
      // if there is not any other RejectTaskQueueElement with same taskId, element can be enqueued
      return (
        this.queue
          .elements()
          .filter((qe) => qe instanceof RejectTaskQueueElement)
          .filter((qe: RejectTaskQueueElement) => qe.task.id === taskId).length === 0
      );
      // } else if (element instanceof CreateTaskQueueElement) {
    }

    return true;
  }

  private queueSize(): number {
    if (this.queue === null) {
      return 0;
    }

    return this.queue.size();
  }

  private taskQueueElementCanBeProcessed(taskQueueElement: TaskQueueElement, index: number): boolean {
    // if there is any other previous element in queue with the taskId, we cannot process this element
    const queueElements = this.queue.elements();
    for (let i = 0; i < index; i++) {
      const qe = queueElements[i];
      if (qe instanceof TaskQueueElement) {
        if (qe.task.id === taskQueueElement.task.id) {
          return false;
        }
      }
    }

    return true;
  }
}
